/**
 * Head (title, description, keywords) and hero of the DirectorHome alias
 * pages /ai-reel-maker and /ai-music-visualizer in the 13 other site
 * languages (Oct 26 2026, Kitty: "translate and index the new pages"). The
 * server prerender (server/_core/seo.ts buildDirectorAliasSeo), the head the
 * client restores (shared/sharedComponentRouteSeo.ts) and the hero
 * DirectorHome draws read these, so hydration cannot swap a string. Small on
 * purpose: DirectorHome is the homepage too. The body, breadcrumb and CTA of
 * each language are lazy chunks in shared/directorAliasLocales/{lang}.ts.
 *
 * English stays where it was (shared/reelMakerSeo.ts,
 * shared/musicVisualizerSeo.ts, shared/sharedComponentRouteSeo.ts); a
 * language missing here serves the English copy. Every number and fact is
 * the English page's (pinned per language in seoReelMaker.test.ts and
 * seoMusicVisualizer.test.ts). Titles are at most 60 characters (32 in
 * Japanese, Korean and Chinese) and descriptions 160 (80).
 */
import type { DirectorAliasHero, FreeMvSeoBody } from "./freeMusicVideoSeo.js";

export const DIRECTOR_ALIAS_PATHS = ["/ai-reel-maker", "/ai-music-visualizer"] as const;
export type DirectorAliasPath = (typeof DIRECTOR_ALIAS_PATHS)[number];

export type DirectorAliasHead = {
  title: string;
  description: string;
  keywords: string[];
  hero: DirectorAliasHero;
};

/** A language's body for one alias page (shared/directorAliasLocales). */
export type DirectorAliasBody = {
  body: FreeMvSeoBody;
  /** The page's name in the breadcrumb. */
  breadcrumb: string;
  /** The crawler page's link to the music video generator. */
  ctaLabel: string;
};

export const DIRECTOR_ALIAS_HEADS: Record<
  DirectorAliasPath,
  Record<string, DirectorAliasHead>
> = {
  "/ai-reel-maker": {
    // Spanish
    es: {
      "title": "Crear reels con IA: convierte cualquier canción en un reel",
      "description": "Crea reels con IA: Solmi convierte tu canción en un video musical vertical para Reels, TikTok y Shorts, editado al ritmo y contigo en pantalla. Empieza gratis.",
      "keywords": [
        "crear reels con ia",
        "creador de reels con ia",
        "generador de reels con ia",
        "hacer reels con ia",
        "ia para crear reels",
        "crear reels para instagram",
        "crear reels con música"
      ],
      "hero": {
        "title": "Convierte cualquier canción en un",
        "highlight": "reel.",
        "subtitle": "Trae una canción y describe el ambiente. Nuestro agente planifica un video musical vertical para Reels, TikTok y Shorts, con cortes al ritmo.",
        "placeholder": "El ambiente: quién sale en pantalla, dónde y qué pasa.",
        "cta": "Crear reel"
      }
    },
    // Portuguese
    pt: {
      "title": "Criar reels com IA: transforme qualquer música em um reel",
      "description": "Crie reels com IA: a Solmi transforma sua música em um clipe vertical para Reels, TikTok e Shorts, editado no ritmo e com você na tela. Grátis para começar.",
      "keywords": [
        "criar reels com ia",
        "criador de reels com ia",
        "gerador de reels com ia",
        "fazer reels com ia",
        "ia para criar reels",
        "criar reels para instagram",
        "criar reels com música"
      ],
      "hero": {
        "title": "Transforme qualquer música em um",
        "highlight": "reel.",
        "subtitle": "Traga uma música e descreva o clima. Nosso agente planeja um clipe vertical para Reels, TikTok e Shorts, com cortes no ritmo da batida.",
        "placeholder": "O clima: quem aparece na tela, onde e o que acontece.",
        "cta": "Criar reel"
      }
    },
    // French
    fr: {
      "title": "Créer des reels avec l'IA : chaque chanson devient un reel",
      "description": "Créez des reels avec l'IA : votre chanson en clip vertical pour Reels, TikTok et Shorts, calé sur le rythme, avec vous à l'écran. Gratuit pour commencer.",
      "keywords": [
        "créer des reels avec l'ia",
        "créateur de reels ia",
        "générateur de reels ia",
        "créer un reel avec l'ia",
        "ia pour faire des reels",
        "créer un reel instagram",
        "faire un reel avec une musique"
      ],
      "hero": {
        "title": "Transformez chaque chanson en",
        "highlight": "reel.",
        "subtitle": "Apportez une chanson et décrivez l'ambiance. Notre agent planifie un clip vertical pour Reels, TikTok et Shorts, avec des coupes calées sur le rythme.",
        "placeholder": "L'ambiance : qui est à l'écran, où, et ce qui se passe.",
        "cta": "Créer le reel"
      }
    },
    // Italian
    it: {
      "title": "Creare reel con AI: trasforma ogni canzone in un reel",
      "description": "Crea reel con AI: Solmi trasforma ogni canzone in un videoclip verticale per Reels, TikTok e Shorts, a tempo di musica e con te in scena. Gratis per iniziare.",
      "keywords": [
        "creare reel con ai",
        "generatore di reel ai",
        "reel con ai",
        "creare reel con intelligenza artificiale",
        "app per creare reel con ai",
        "creare reel instagram con ai",
        "creare reel con musica"
      ],
      "hero": {
        "title": "Trasforma ogni canzone in un",
        "highlight": "reel.",
        "subtitle": "Porta una canzone e descrivi l'atmosfera. Il nostro agente pianifica un video musicale verticale per Reels, TikTok e Shorts, montato a tempo di musica.",
        "placeholder": "L'atmosfera: chi c'è in scena, dove e cosa succede.",
        "cta": "Crea il reel"
      }
    },
    // German
    de: {
      "title": "Reels mit KI erstellen: aus jedem Song ein Reel",
      "description": "Reels mit KI erstellen: Aus jedem Song wird ein vertikales Musikvideo für Reels, TikTok und Shorts, im Takt geschnitten, mit dir im Bild. Kostenlos starten.",
      "keywords": [
        "reels mit ki erstellen",
        "ki reels erstellen",
        "ki reel generator",
        "ki reel maker",
        "instagram reels mit ki erstellen",
        "reel aus song erstellen",
        "reels mit musik erstellen"
      ],
      "hero": {
        "title": "Verwandle jeden Song in ein",
        "highlight": "Reel.",
        "subtitle": "Bring einen Song mit und beschreibe die Stimmung. Unser Agent plant ein vertikales Musikvideo für Reels, TikTok und Shorts, im Takt geschnitten.",
        "placeholder": "Die Stimmung: wer im Bild ist, wo, und was passiert.",
        "cta": "Reel erstellen"
      }
    },
    // Dutch
    nl: {
      "title": "Reels maken met AI: van elk nummer een reel",
      "description": "Reels maken met AI: Solmi maakt van elk nummer een verticale muziekvideo voor Reels, TikTok en Shorts, op de beat gemonteerd, met jou in beeld. Gratis starten.",
      "keywords": [
        "reels maken met ai",
        "ai reel maker",
        "ai reels maken",
        "ai reel generator",
        "instagram reels maken met ai",
        "reels maken met muziek",
        "reel maken van een nummer"
      ],
      "hero": {
        "title": "Maak van elk nummer een",
        "highlight": "reel.",
        "subtitle": "Breng een nummer mee en beschrijf de sfeer. Onze agent plant een verticale muziekvideo voor Reels, TikTok en Shorts, op de beat gemonteerd.",
        "placeholder": "De sfeer: wie er in beeld is, waar en wat er gebeurt.",
        "cta": "Reel maken"
      }
    },
    // Polish
    pl: {
      "title": "Generator rolek AI: zamień każdą piosenkę w rolkę",
      "description": "Generator rolek AI: Solmi zamienia każdą piosenkę w pionowy teledysk na Reels, TikToka i Shorts, zmontowany w rytm muzyki, z Tobą na ekranie. Zacznij za darmo.",
      "keywords": [
        "generator rolek ai",
        "rolki ai",
        "tworzenie rolek ai",
        "rolki na instagram ai",
        "jak zrobić rolkę z piosenki",
        "rolka z muzyką",
        "ai do tworzenia rolek"
      ],
      "hero": {
        "title": "Zamień każdą piosenkę w",
        "highlight": "rolkę.",
        "subtitle": "Przynieś piosenkę i opisz klimat. Nasz agent zaplanuje pionowy teledysk na Reels, TikToka i Shorts, zmontowany w rytm muzyki.",
        "placeholder": "Klimat: kto jest na ekranie, gdzie i co się dzieje.",
        "cta": "Stwórz rolkę"
      }
    },
    // Turkish
    tr: {
      "title": "Yapay Zeka ile Reels Yapma: Her Şarkıyı Reels'e Dönüştür",
      "description": "Yapay zeka ile Reels yap: Solmi her şarkıyı Reels, TikTok ve Shorts için ritme uyan dikey bir klibe çevirir; tek fotoğrafla sen de ekrandasın. Ücretsiz başla.",
      "keywords": [
        "yapay zeka ile reels yapma",
        "yapay zeka reels oluşturucu",
        "reels yapma",
        "ai reels yapma",
        "instagram reels yapma",
        "şarkıyla reels yapma",
        "müzikli reels yapma"
      ],
      "hero": {
        "title": "Her şarkıyı",
        "highlight": "Reels'e dönüştür.",
        "subtitle": "Bir şarkı getir ve havasını anlat. Ajanımız Reels, TikTok ve Shorts için ritme göre kesilmiş dikey bir müzik videosu planlar.",
        "placeholder": "Havası: ekranda kim var, nerede ve neler oluyor.",
        "cta": "Reels yap"
      }
    },
    // Indonesian
    id: {
      "title": "Pembuat Reels AI: ubah lagu apa pun jadi video Reels",
      "description": "Pembuat Reels AI Solmi: lagu apa pun jadi video musik vertikal sesuai beat untuk Reels, TikTok, dan Shorts, dan kamu bisa tampil dari 1 foto. Mulai gratis.",
      "keywords": [
        "pembuat reels ai",
        "ai reels maker",
        "buat reels dengan ai",
        "cara membuat reels",
        "buat video reels",
        "pembuat reels instagram",
        "buat reels dari lagu"
      ],
      "hero": {
        "title": "Ubah lagu apa pun menjadi",
        "highlight": "Reels.",
        "subtitle": "Bawa sebuah lagu dan jelaskan suasananya. Agen kami merancang video musik vertikal untuk Reels, TikTok, dan Shorts, dipotong sesuai beat.",
        "placeholder": "Suasananya: siapa yang tampil, di mana, dan apa yang terjadi.",
        "cta": "Buat Reels"
      }
    },
    // Vietnamese
    vi: {
      "title": "Tạo video Reels bằng AI: biến mọi bài hát thành Reels",
      "description": "Tạo Reels bằng AI: Solmi biến mọi bài hát thành video âm nhạc dọc cắt theo nhịp cho Reels, TikTok và Shorts, có bạn xuất hiện từ một ảnh. Bắt đầu miễn phí.",
      "keywords": [
        "tạo reels bằng ai",
        "tạo video reels bằng ai",
        "ai reels maker",
        "làm video reels",
        "app tạo reels",
        "tạo reels instagram",
        "làm reels từ bài hát"
      ],
      "hero": {
        "title": "Biến mọi bài hát thành",
        "highlight": "video Reels.",
        "subtitle": "Mang một bài hát đến và mô tả không khí bạn muốn. Agent của chúng tôi lên kế hoạch cho một video âm nhạc dọc, cắt theo nhịp, dành cho Reels, TikTok và Shorts.",
        "placeholder": "Không khí: ai xuất hiện, ở đâu và chuyện gì xảy ra.",
        "cta": "Tạo video Reels"
      }
    },
    // Japanese
    ja: {
      "title": "AIリール動画作成｜曲から縦型MVを自動生成",
      "description": "SolmiのAIリール動画作成なら、どんな曲もリール・TikTok・ショート向けの縦型MVに。ビートに合わせたカットで、写真1枚からあなたも出演。無料で開始。",
      "keywords": [
        "aiリール動画作成",
        "リール動画 作成 ai",
        "ai リール 作成",
        "リール動画メーカー",
        "インスタ リール 作成",
        "曲 リール動画 作成",
        "ショート動画 作成 ai"
      ],
      "hero": {
        "title": "どんな曲も",
        "highlight": "リール動画に。",
        "subtitle": "曲を持ち込んで、雰囲気を伝えてください。エージェントが、リール・TikTok・ショート向けの縦型MVを、ビートに合わせたカットで企画します。",
        "placeholder": "雰囲気：誰が映るか、どこで、何が起きるか。",
        "cta": "リール動画を作る"
      }
    },
    // Korean
    ko: {
      "title": "AI 릴스 만들기 | 노래로 세로형 뮤직비디오 생성",
      "description": "AI 릴스 만들기: 어떤 노래든 릴스, TikTok, 쇼츠용 세로형 뮤직비디오로. 비트에 맞춘 컷, 사진 한 장으로 나도 출연. 무료로 시작.",
      "keywords": [
        "ai 릴스 만들기",
        "릴스 만들기 ai",
        "ai 릴스 메이커",
        "인스타 릴스 만들기",
        "노래로 릴스 만들기",
        "음악 릴스 만들기",
        "ai 숏폼 만들기"
      ],
      "hero": {
        "title": "어떤 노래든",
        "highlight": "릴스로.",
        "subtitle": "노래를 가져와 분위기를 설명하세요. 에이전트가 릴스, TikTok, 쇼츠용 세로형 뮤직비디오를 비트에 맞춰 기획합니다.",
        "placeholder": "분위기: 누가 나오고, 어디서, 무슨 일이 일어나는지.",
        "cta": "릴스 만들기"
      }
    },
    // Simplified Chinese
    zh: {
      "title": "AI Reels 制作：任何歌曲都能生成竖屏 MV",
      "description": "Solmi 的 AI Reels 制作工具把任何歌曲做成适合 Reels、TikTok 和 Shorts 的竖屏 MV：画面卡点，一张照片就能出镜。免费开始。",
      "keywords": [
        "ai reels 制作",
        "ai reels 生成器",
        "reels 制作",
        "instagram reels 制作",
        "ai 短视频生成器",
        "竖屏 mv 制作",
        "音乐短视频制作"
      ],
      "hero": {
        "title": "把任何一首歌变成",
        "highlight": "Reels 短视频。",
        "subtitle": "带来一首歌，描述你想要的氛围。我们的 AI 智能体将为 Reels、TikTok 和 Shorts 规划一支卡点的竖屏 MV。",
        "placeholder": "氛围：谁出镜、在哪里、发生什么。",
        "cta": "制作 Reels"
      }
    },
  },
  "/ai-music-visualizer": {
    // Spanish
    es: {
      "title": "Visualizador de música con IA: visuales abstractos",
      "description": "Visualizador de música con IA: convierte tu canción en un video abstracto de formas, luz y color que se mueven con la música, sin personas. Empieza gratis.",
      "keywords": [
        "visualizador de música con ia",
        "visualizador musical con ia",
        "visualizador de música con ia gratis",
        "visualizador de audio con ia",
        "generador de visualizadores con ia",
        "video musical abstracto"
      ],
      "hero": {
        "title": "Convierte cualquier canción en un",
        "highlight": "visualizador musical.",
        "subtitle": "Trae una pista y describe el look. Nuestro agente planifica visuales abstractos que se mueven con la música, sin personas en pantalla.",
        "placeholder": "Colores, formas, texturas: lo que debe moverse con la música.",
        "cta": "Crear visualizador"
      }
    },
    // Portuguese
    pt: {
      "title": "Visualizador de música com IA: visuais abstratos",
      "description": "Visualizador de música com IA: transforme sua música em um vídeo abstrato de formas, luz e cor que se movem com o som, sem pessoas na tela. Grátis para começar.",
      "keywords": [
        "visualizador de música com ia",
        "visualizador musical com ia",
        "visualizador de música com ia grátis",
        "visualizador de áudio com ia",
        "gerador de visualizador com ia",
        "videoclipe abstrato"
      ],
      "hero": {
        "title": "Transforme qualquer música em um",
        "highlight": "visualizador musical.",
        "subtitle": "Traga uma faixa e descreva o visual. Nosso agente planeja visuais abstratos que se movem com a música, sem pessoas na tela.",
        "placeholder": "Cores, formas, texturas: o que deve se mover com a música.",
        "cta": "Criar visualizador"
      }
    },
    // French
    fr: {
      "title": "Visualiseur musical IA : visuels abstraits pour vos chansons",
      "description": "Visualiseur musical IA : votre chanson en vidéo abstraite de formes, de lumière et de couleurs qui suivent la musique, sans personne. Gratuit pour commencer.",
      "keywords": [
        "visualiseur musical ia",
        "visualiseur de musique ia",
        "visualiseur audio ia",
        "visualiseur musical ia gratuit",
        "générateur de visualiseur ia",
        "clip musical abstrait"
      ],
      "hero": {
        "title": "Transformez chaque chanson en",
        "highlight": "visualiseur musical.",
        "subtitle": "Apportez un morceau et décrivez le rendu. Notre agent planifie des visuels abstraits qui bougent avec la musique, sans personne à l'écran.",
        "placeholder": "Couleurs, formes, textures : ce qui doit bouger avec la musique.",
        "cta": "Créer le visualiseur"
      }
    },
    // Italian
    it: {
      "title": "Visualizzatore musicale AI: video astratti per ogni canzone",
      "description": "Visualizzatore musicale AI: la tua canzone diventa un video astratto di forme, luce e colore che si muovono con la musica, senza persone. Gratis per iniziare.",
      "keywords": [
        "visualizzatore musicale ai",
        "visualizer musicale ai",
        "visualizzatore audio ai",
        "visualizzatore musicale ai gratis",
        "generatore di visualizer ai",
        "video musicale astratto"
      ],
      "hero": {
        "title": "Trasforma ogni canzone in un",
        "highlight": "visualizzatore musicale.",
        "subtitle": "Porta un brano e descrivi il look. Il nostro agente pianifica immagini astratte che si muovono con la musica, senza persone in scena.",
        "placeholder": "Colori, forme, texture: cosa deve muoversi con la musica.",
        "cta": "Crea il visualizzatore"
      }
    },
    // German
    de: {
      "title": "KI-Musik-Visualizer: abstrakte Visuals für jeden Song",
      "description": "KI-Musik-Visualizer: Aus jedem Song wird ein abstraktes Video aus Formen, Licht und Farbe, das sich mit der Musik bewegt, ohne Menschen. Kostenlos starten.",
      "keywords": [
        "ki musik visualizer",
        "musik visualizer ki",
        "ki audio visualizer",
        "ki visualizer generator",
        "ki musik visualizer kostenlos",
        "abstraktes musikvideo"
      ],
      "hero": {
        "title": "Verwandle jeden Song in einen",
        "highlight": "Musik-Visualizer.",
        "subtitle": "Bring einen Track mit und beschreibe den Look. Unser Agent plant abstrakte Visuals, die sich mit der Musik bewegen, ganz ohne Menschen im Bild.",
        "placeholder": "Farben, Formen, Texturen: was sich mit der Musik bewegen soll.",
        "cta": "Visualizer erstellen"
      }
    },
    // Dutch
    nl: {
      "title": "AI muziek visualizer: abstracte beelden bij elk nummer",
      "description": "AI muziek visualizer: Solmi maakt van elk nummer een abstracte video van vormen, licht en kleur die met de muziek meebewegen, zonder mensen. Gratis starten.",
      "keywords": [
        "ai muziek visualizer",
        "ai audio visualizer",
        "ai visualizer generator",
        "gratis ai muziek visualizer",
        "muziekvisualisatie met ai",
        "abstracte muziekvideo"
      ],
      "hero": {
        "title": "Maak van elk nummer een",
        "highlight": "muziekvisualizer.",
        "subtitle": "Breng een track mee en beschrijf de look. Onze agent plant abstracte beelden die met de muziek meebewegen, zonder mensen in beeld.",
        "placeholder": "Kleuren, vormen, texturen: wat moet er met de muziek meebewegen?",
        "cta": "Visualizer maken"
      }
    },
    // Polish
    pl: {
      "title": "Wizualizer muzyki AI: abstrakcyjne wideo do każdej piosenki",
      "description": "Wizualizer muzyki AI: Solmi zamienia każdą piosenkę w abstrakcyjne wideo z kształtów, światła i koloru poruszających się z muzyką, bez ludzi. Zacznij za darmo.",
      "keywords": [
        "wizualizer muzyki ai",
        "wizualizacja muzyki ai",
        "wizualizer audio ai",
        "darmowy wizualizer muzyki ai",
        "generator wizualizacji ai",
        "abstrakcyjny teledysk"
      ],
      "hero": {
        "title": "Zamień każdą piosenkę w",
        "highlight": "wizualizer muzyki.",
        "subtitle": "Przynieś utwór i opisz wygląd. Nasz agent zaplanuje abstrakcyjny obraz, który porusza się z muzyką, bez ludzi na ekranie.",
        "placeholder": "Kolory, kształty, faktury: co ma się poruszać z muzyką.",
        "cta": "Stwórz wizualizer"
      }
    },
    // Turkish
    tr: {
      "title": "Yapay Zeka Müzik Görselleştirici: Soyut Müzik Videosu",
      "description": "Yapay zeka müzik görselleştirici: her şarkıyı müzikle hareket eden şekil, ışık ve renklerden oluşan soyut bir videoya çevir; ekranda insan yok. Ücretsiz başla.",
      "keywords": [
        "yapay zeka müzik görselleştirici",
        "yapay zeka görselleştirici",
        "yapay zeka ses görselleştirici",
        "ai music visualizer",
        "ai visualizer",
        "soyut müzik videosu",
        "müzik görselleştirme videosu"
      ],
      "hero": {
        "title": "Her şarkıyı",
        "highlight": "görselleştirici videoya dönüştür.",
        "subtitle": "Bir parça getir ve görünümü anlat. Ajanımız müzikle hareket eden soyut görseller planlar; ekranda hiç insan olmaz.",
        "placeholder": "Renkler, şekiller, dokular: müzikle ne hareket etmeli.",
        "cta": "Görselleştirici yap"
      }
    },
    // Indonesian
    id: {
      "title": "Visualizer musik AI: visual abstrak untuk lagu apa pun",
      "description": "Visualizer musik AI Solmi mengubah lagu apa pun jadi video abstrak berisi bentuk, cahaya, dan warna yang bergerak mengikuti musik, tanpa orang. Mulai gratis.",
      "keywords": [
        "visualizer musik ai",
        "ai music visualizer",
        "ai audio visualizer",
        "pembuat visualizer ai",
        "visualizer lagu ai",
        "ai visualizer generator",
        "video musik abstrak"
      ],
      "hero": {
        "title": "Ubah lagu apa pun menjadi",
        "highlight": "visualizer musik.",
        "subtitle": "Bawa sebuah lagu dan jelaskan tampilannya. Agen kami merancang visual abstrak yang bergerak mengikuti musik, tanpa orang di layar.",
        "placeholder": "Warna, bentuk, tekstur: apa yang bergerak mengikuti musik.",
        "cta": "Buat visualizer"
      }
    },
    // Vietnamese
    vi: {
      "title": "Tạo visualizer nhạc bằng AI: video trừu tượng từ mọi bài hát",
      "description": "Visualizer nhạc bằng AI: Solmi biến mọi bài hát thành video trừu tượng gồm hình khối, ánh sáng, màu sắc chuyển động theo nhạc, không có người. Bắt đầu miễn phí.",
      "keywords": [
        "tạo visualizer nhạc bằng ai",
        "visualizer nhạc ai",
        "ai music visualizer",
        "ai audio visualizer",
        "tạo visualizer bằng ai",
        "video nhạc trừu tượng",
        "mv trừu tượng"
      ],
      "hero": {
        "title": "Biến mọi bài hát thành",
        "highlight": "visualizer âm nhạc.",
        "subtitle": "Mang một bài hát đến và mô tả phong cách hình ảnh. Agent của chúng tôi lên kế hoạch cho những hình ảnh trừu tượng chuyển động theo nhạc, không có người trên màn hình.",
        "placeholder": "Màu sắc, hình khối, chất liệu: thứ gì sẽ chuyển động theo nhạc.",
        "cta": "Tạo visualizer"
      }
    },
    // Japanese
    ja: {
      "title": "AI音楽ビジュアライザー｜どんな曲も抽象映像のMVに",
      "description": "SolmiのAI音楽ビジュアライザーなら、どんな曲も、形・光・色が音楽に合わせて動く抽象的な映像に。人物は映りません。無料で開始。",
      "keywords": [
        "ai 音楽ビジュアライザー",
        "ai ビジュアライザー",
        "音楽ビジュアライザー ai",
        "ビジュアライザー 作成 ai",
        "ai オーディオビジュアライザー",
        "ビジュアライザー mv",
        "抽象 mv 作成"
      ],
      "hero": {
        "title": "どんな曲も",
        "highlight": "音楽ビジュアライザーに。",
        "subtitle": "曲を持ち込んで、見た目のイメージを伝えてください。エージェントが、音楽に合わせて動く抽象的な映像を企画します。画面に人物は登場しません。",
        "placeholder": "色、形、質感：音楽に合わせて何を動かすか。",
        "cta": "ビジュアライザーを作る"
      }
    },
    // Korean
    ko: {
      "title": "AI 음악 비주얼라이저 | 어떤 노래든 추상 영상으로",
      "description": "AI 음악 비주얼라이저: 어떤 노래든 음악에 맞춰 움직이는 도형, 빛, 색의 추상 영상으로. 화면에 사람은 나오지 않습니다. 무료로 시작.",
      "keywords": [
        "ai 음악 비주얼라이저",
        "ai 비주얼라이저",
        "음악 비주얼라이저 ai",
        "ai 오디오 비주얼라이저",
        "비주얼라이저 만들기",
        "비주얼라이저 영상 만들기",
        "추상 뮤직비디오"
      ],
      "hero": {
        "title": "어떤 노래든",
        "highlight": "음악 비주얼라이저로.",
        "subtitle": "곡을 가져와 원하는 룩을 설명하세요. 에이전트가 음악에 맞춰 움직이는 추상 비주얼을 기획하며, 화면에 사람은 나오지 않습니다.",
        "placeholder": "색, 도형, 질감: 음악에 맞춰 무엇이 움직여야 할지.",
        "cta": "비주얼라이저 만들기"
      }
    },
    // Simplified Chinese
    zh: {
      "title": "AI 音乐可视化：把任何歌曲变成抽象视觉 MV",
      "description": "Solmi 的 AI 音乐可视化工具，把任何歌曲变成由形状、光影和色彩组成、随音乐律动的抽象视频，画面中没有人物。免费开始。",
      "keywords": [
        "ai 音乐可视化",
        "ai 音乐可视化生成器",
        "音乐可视化 ai",
        "ai 音频可视化",
        "ai 可视化视频",
        "音乐可视化视频制作",
        "抽象 mv"
      ],
      "hero": {
        "title": "把任何一首歌变成",
        "highlight": "音乐可视化视频。",
        "subtitle": "带来一首歌，描述你想要的视觉效果。我们的 AI 智能体将规划随音乐律动的抽象画面，画面中没有人物。",
        "placeholder": "色彩、形状、质感：什么该随音乐律动。",
        "cta": "制作可视化视频"
      }
    },
  },
};

/** The translated head of an alias page, or null for English and the rest. */
export function getDirectorAliasHead(path: string, lang?: string): DirectorAliasHead | null {
  if (!lang || lang === "en") return null;
  return (DIRECTOR_ALIAS_HEADS as Record<string, Record<string, DirectorAliasHead>>)[path]?.[lang] ?? null;
}
